// Integrations page — full catalog of 46+ integrations grouped by category with detail modal
import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import * as Icons from "lucide-react";
import { Search, Check, X, Zap, ArrowRight, KeyRound, Lock } from "lucide-react";
import { toast } from "sonner";

// Resolve a lucide icon by name (fallback to Plug)
const Ic = (name) => {
  const map = {
    "users-2": Icons.Users2, kanban: Icons.Kanban, cloud: Icons.Cloud, calculator: Icons.Calculator,
    leaf: Icons.Leaf, "credit-card": Icons.CreditCard, wallet: Icons.Wallet,
    "shopping-bag": Icons.ShoppingBag, "shopping-cart": Icons.ShoppingCart,
    "message-circle": Icons.MessageCircle, send: Icons.Send, smartphone: Icons.Smartphone,
    "message-square": Icons.MessageSquare, headphones: Icons.Headphones,
    table: Icons.Table, folder: Icons.Folder, "list-checks": Icons.ListChecks,
    "layout-grid": Icons.LayoutGrid, database: Icons.Database, "check-square": Icons.CheckSquare,
    bug: Icons.Bug, "book-open": Icons.BookOpen, mail: Icons.Mail, activity: Icons.Activity,
    "clipboard-list": Icons.ClipboardList, globe: Icons.Globe, users: Icons.Users,
    briefcase: Icons.Briefcase, "file-signature": Icons.FileSignature, linkedin: Icons.Linkedin,
    instagram: Icons.Instagram, facebook: Icons.Facebook, twitter: Icons.Twitter,
    video: Icons.Video, "calendar-check": Icons.CalendarCheck, calendar: Icons.Calendar,
    webhook: Icons.Webhook,
  };
  return map[name] || Icons.Plug;
};

const COLOR_BG = {
  orange: "bg-orange-500", green: "bg-emerald-500", blue: "bg-blue-500", indigo: "bg-indigo-500",
  emerald: "bg-emerald-600", violet: "bg-violet-500", purple: "bg-purple-500", red: "bg-red-500",
  pink: "bg-pink-500", yellow: "bg-yellow-500", zinc: "bg-zinc-700",
};

// Provider-specific helper text shown in the API key modal
const API_KEY_HELP = {
  brevo: {
    label: "Clé API Brevo",
    placeholder: "xkeysib-xxxxxxxxxxxxxxxxxxxx",
    help: "Récupérez votre clé sur Brevo → SMTP & API → Clés API → Créer une nouvelle clé.",
    url: "https://app.brevo.com/settings/keys/api",
  },
  yousign: {
    label: "Clé API Yousign",
    placeholder: "yk_xxxxxxxxxxxxxxxxxxxx",
    help: "Récupérez votre clé sur Yousign → Paramètres → API → Créer une clé.",
    url: "https://yousign.app/settings/api-keys",
  },
  pennylane: {
    label: "Clé API Pennylane",
    placeholder: "Bearer ...",
    help: "Demandez votre clé d'API à Pennylane (Compte → Intégrations → API → Token personnel).",
    url: "https://pennylane.com/",
  },
  trello: {
    label: "Clé API Trello",
    placeholder: "abcdef0123456789...",
    help: "Récupérez votre clé sur la page Power-Up Admin de Trello. Le token sera demandé juste après.",
    url: "https://trello.com/power-ups/admin",
    token_label: "Token Trello",
    token_placeholder: "ATTAxxxxxxxxxxxxxxxxxx...",
    token_help: "Sur la même page, cliquez sur 'Token' pour générer un token API personnel (permission 'read,write,account', sans expiration).",
  },
};

export default function Integrations() {
  const [catalog, setCatalog] = useState({ categories: [], integrations: [] });
  const [connected, setConnected] = useState({});
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("Tous");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [myVotes, setMyVotes] = useState(new Set());
  const [voteCount, setVoteCount] = useState({});
  const [voting, setVoting] = useState(null);
  // API key modal state
  const [apiKeyModal, setApiKeyModal] = useState({ open: false, slug: null });
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [apiTokenValue, setApiTokenValue] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  // Shopify shop modal state
  const [shopModal, setShopModal] = useState({ open: false, slug: null });
  const [shopValue, setShopValue] = useState("");
  const [startingShop, setStartingShop] = useState(false);
  // Live connection test state
  const [connectionTest, setConnectionTest] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);

  const load = async () => {
    setLoading(true);
    const [c, s, v] = await Promise.all([
      api.get("/integrations/catalog").then((r) => r.data),
      api.get("/integrations").then((r) => r.data),
      api.get("/integrations/votes").then((r) => r.data).catch(() => ({ ranking: [], my_votes: [] })),
    ]);
    setCatalog(c);
    const map = {};
    s.forEach((x) => {
      if (x.connected) {
        map[x.slug] = { account_label: x.account_label, connected_at: x.connected_at };
      }
    });
    setConnected(map);
    setMyVotes(new Set(v.my_votes || []));
    const counts = {};
    (v.ranking || []).forEach((r) => { counts[r.slug] = r.votes; });
    setVoteCount(counts);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const vote = async (slug) => {
    if (myVotes.has(slug)) {
      toast.info("Vous avez déjà voté pour cette intégration ✓");
      return;
    }
    setVoting(slug);
    try {
      const { data } = await api.post(`/integrations/${slug}/vote`);
      if (data.success) {
        toast.success("Merci pour votre vote ! 🎉");
        setMyVotes(new Set([...myVotes, slug]));
        setVoteCount({ ...voteCount, [slug]: data.votes });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setVoting(null);
    }
  };

  const filtered = useMemo(() => {
    return catalog.integrations.filter((i) => {
      if (cat !== "Tous" && i.category !== cat) return false;
      if (q && !(i.name + i.description + i.category).toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [catalog, cat, q]);

  const toggle = async (slug) => {
    const ig = catalog.integrations.find((x) => x.slug === slug);
    if (ig?.status === "coming_soon") {
      toast.info("Cette intégration arrive très bientôt 🚀");
      return;
    }
    // If already connected, simple disconnect.
    if (connected[slug]) {
      setConnecting(slug);
      try {
        await api.post(`/integrations/${slug}/disconnect`);
        toast.success("Déconnecté");
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Échec");
      } finally {
        setConnecting(null);
      }
      return;
    }
    // Shopify needs the shop name first → open prompt modal
    if (slug === "shopify") {
      setShopValue("");
      setShopModal({ open: true, slug });
      return;
    }
    // API-key integrations → open key prompt modal
    if (ig?.auth === "api_key" || API_KEY_HELP[slug]) {
      setApiKeyValue("");
      setApiTokenValue("");
      setApiKeyModal({ open: true, slug });
      return;
    }
    // Default: call connect to get OAuth URL
    setConnecting(slug);
    try {
      const { data } = await api.post(`/integrations/${slug}/connect`);
      if (data.requires_api_key) {
        setApiKeyValue("");
        setApiKeyModal({ open: true, slug });
        return;
      }
      if (data.requires_shop) {
        setShopValue("");
        setShopModal({ open: true, slug });
        return;
      }
      if (data.oauth_url) {
        window.location.href = data.oauth_url;
        return;
      }
      if (data.redirect_to) {
        window.location.href = data.redirect_to;
        return;
      }
      toast.success("Connecté");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setConnecting(null);
    }
  };

  // Submit API key (Brevo / Yousign / Pennylane / Trello)
  const submitApiKey = async () => {
    if (!apiKeyValue.trim()) {
      toast.error("Veuillez saisir une clé API.");
      return;
    }
    const meta = API_KEY_HELP[apiKeyModal.slug] || {};
    if (meta.token_label && !apiTokenValue.trim()) {
      toast.error(`Veuillez saisir le ${meta.token_label.toLowerCase()}.`);
      return;
    }
    setSavingKey(true);
    try {
      const payload = { api_key: apiKeyValue.trim() };
      if (apiTokenValue.trim()) payload.api_token = apiTokenValue.trim();
      await api.post(`/integrations/${apiKeyModal.slug}/connect-apikey`, payload);
      toast.success(`${apiKeyModal.slug} connecté avec succès !`);
      setApiKeyModal({ open: false, slug: null });
      setApiKeyValue("");
      setApiTokenValue("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clé invalide");
    } finally {
      setSavingKey(false);
    }
  };

  // Submit Shopify shop name → starts OAuth flow with ?shop=
  const submitShop = async () => {
    const raw = shopValue.trim().toLowerCase();
    if (!raw) {
      toast.error("Veuillez saisir le nom de votre boutique.");
      return;
    }
    // Strip ".myshopify.com" and ".com" / scheme if user pasted full URL
    const shop = raw
      .replace(/^https?:\/\//, "")
      .replace(/\.myshopify\.com.*$/, "")
      .replace(/\/.*$/, "");
    setStartingShop(true);
    try {
      const { data } = await api.post(`/integrations/${shopModal.slug}/connect`, null, { params: { shop } });
      if (data.oauth_url) {
        window.location.href = data.oauth_url;
        return;
      }
      toast.error("Impossible de démarrer la connexion Shopify");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec Shopify");
    } finally {
      setStartingShop(false);
    }
  };

  // Live API ping of the connected provider — proves the tokens really work
  const testConnection = async (slug) => {
    setTestingConnection(true);
    setConnectionTest(null);
    try {
      const { data } = await api.post(`/integrations/${slug}/test-connection`);
      setConnectionTest({ ...data, slug });
      if (data.success) {
        toast.success(data.tested_live ? "Connexion validée par appel API en direct ✓" : "Métadonnées de connexion chargées ✓");
      } else {
        toast.error(data.message || "Connexion invalide");
      }
    } catch (e) {
      const errMsg = e?.response?.data?.detail || "Échec";
      setConnectionTest({ slug, success: false, error: errMsg, message: errMsg });
      toast.error(errMsg);
    } finally {
      setTestingConnection(false);
    }
  };

  // Reconnect (disconnect + re-trigger connect flow in one action)
  const reconnect = async (slug) => {
    setConnecting(slug);
    try {
      await api.post(`/integrations/${slug}/disconnect`).catch(() => {});
      setConnected((prev) => {
        const next = { ...prev };
        delete next[slug];
        return next;
      });
      const { data } = await api.post(`/integrations/${slug}/connect`);
      if (data.requires_api_key) {
        setApiKeyValue("");
        setApiKeyModal({ open: true, slug });
        return;
      }
      if (data.requires_shop) {
        setShopValue("");
        setShopModal({ open: true, slug });
        return;
      }
      if (data.oauth_url) {
        window.location.href = data.oauth_url;
        return;
      }
      toast.success("Reconnecté");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setConnecting(null);
    }
  };

  // Handle OAuth callback redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("oauth_success");
    const error = params.get("oauth_error");
    if (success) {
      toast.success(`Intégration "${success}" connectée avec succès !`);
      window.history.replaceState({}, "", window.location.pathname);
      load();
    } else if (error) {
      toast.error(`Erreur OAuth : ${error}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const categories = ["Tous", ...catalog.categories];

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="integrations-page">
      <div className="mb-10">
        <span className="overline">Catalogue d&apos;intégrations</span>
        <h1 className="font-display text-4xl font-medium tracking-tighter mt-2">
          {catalog.integrations.length} intégrations connectables
        </h1>
        <p className="text-zinc-600 mt-2 max-w-3xl">
          CRM, comptabilité, e-commerce, RH, signature, marketing — tout votre stack métier en un endroit.
          Chaque intégration offre des actions et des déclencheurs réutilisables dans vos workflows.
        </p>
      </div>

      {/* Search + filters */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-8">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une intégration..." className="rounded-sm pl-9" data-testid="integrations-search"/>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto py-1">
          {categories.map((c) => (
            <button
              key={c} onClick={() => setCat(c)}
              className={`px-3 py-1.5 text-xs rounded-sm whitespace-nowrap transition-colors ${
                cat === c ? "bg-black text-white" : "bg-white border border-zinc-200 text-zinc-700 hover:border-zinc-400"
              }`}
              data-testid={`integrations-cat-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 p-12 text-center">Chargement du catalogue...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((ig, idx) => {
            const Icon = Ic(ig.icon);
            const isConnected = !!connected[ig.slug];
            const isComingSoon = ig.status === "coming_soon";
            const bg = COLOR_BG[ig.color] || "bg-zinc-700";
            return (
              <motion.div
                key={ig.slug}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx * 0.02, 0.4) }}
                whileHover={{ y: -2 }}
                className={`bg-white border rounded-sm p-5 transition-all cursor-pointer ${
                  isComingSoon ? "opacity-75" : ""
                } ${ig.featured ? "border-emerald-300 ring-1 ring-emerald-100" : "border-zinc-200"}`}
                onClick={() => setSelected(ig)}
                data-testid={`integration-card-${ig.slug}`}
              >
                <div className="flex items-start justify-between mb-3">
                  {ig.logo_url ? (
                    <div className="w-11 h-11 bg-white border border-zinc-200 rounded-sm flex items-center justify-center p-1 overflow-hidden">
                      <img src={ig.logo_url} alt={ig.name} className="max-w-full max-h-full object-contain" loading="lazy" onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                      <span className={`hidden w-full h-full ${bg} rounded-sm items-center justify-center text-white`}><Icon size={20} strokeWidth={1.75} /></span>
                    </div>
                  ) : (
                    <div className={`w-11 h-11 ${bg} rounded-sm flex items-center justify-center text-white`}>
                      <Icon size={20} strokeWidth={1.75}/>
                    </div>
                  )}
                  <div className="flex flex-col items-end gap-1">
                    {isComingSoon && <Badge className="bg-amber-100 text-amber-800 rounded-sm font-mono text-[9px]" data-testid={`integration-coming-soon-${ig.slug}`}>BIENTÔT</Badge>}
                    {ig.featured && !isComingSoon && <Badge className="bg-emerald-600 text-white rounded-sm font-mono text-[9px]">SIGNATURE</Badge>}
                    {isConnected && <Badge className="klein-bg text-white rounded-sm font-mono text-[9px]">CONNECTÉ</Badge>}
                  </div>
                </div>
                <p className="font-display font-medium tracking-tight">{ig.name}</p>
                <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider mt-0.5">{ig.category}</p>
                <p className="text-xs text-zinc-600 mt-2 leading-relaxed min-h-[32px] line-clamp-2">{ig.description}</p>

                {isConnected && connected[ig.slug]?.account_label && (
                  <p className="text-[10px] text-emerald-700 font-mono mt-2 truncate" data-testid={`integration-account-label-${ig.slug}`}>
                    ✓ {connected[ig.slug].account_label}
                  </p>
                )}

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-100">
                  <div className="flex items-center gap-3 text-[10px] font-mono text-zinc-500">
                    <span>{ig.actions?.length || 0} actions</span>
                    <span>{ig.triggers?.length || 0} triggers</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isComingSoon) {
                        vote(ig.slug);
                      } else {
                        toggle(ig.slug);
                      }
                    }}
                    disabled={connecting === ig.slug || voting === ig.slug}
                    className={`text-xs px-3 py-1 rounded-sm transition-colors flex items-center gap-1 ${
                      isComingSoon
                        ? myVotes.has(ig.slug)
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default"
                          : "bg-amber-50 border border-amber-300 text-amber-900 hover:bg-amber-100"
                        : isConnected
                          ? "bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                          : "bg-black text-white hover:bg-zinc-800"
                    }`}
                    data-testid={isComingSoon ? `integration-vote-${ig.slug}` : `integration-toggle-${ig.slug}`}
                  >
                    {connecting === ig.slug || voting === ig.slug ? "..." : isComingSoon
                      ? (myVotes.has(ig.slug)
                          ? `✓ Voté${voteCount[ig.slug] ? ` (${voteCount[ig.slug]})` : ""}`
                          : `Voter${voteCount[ig.slug] ? ` (${voteCount[ig.slug]})` : ""}`)
                      : isConnected
                        ? "Déconnecter"
                        : "Connecter"}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setConnectionTest(null); } }}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="integration-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 ${COLOR_BG[selected.color] || "bg-zinc-700"} rounded-sm flex items-center justify-center text-white`}>
                    {(() => { const Icon = Ic(selected.icon); return <Icon size={26} strokeWidth={1.75}/>; })()}
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="font-display text-2xl font-medium tracking-tight">{selected.name}</DialogTitle>
                    <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-1">{selected.category}</p>
                  </div>
                  <Badge className={selected.auth === "oauth" ? "bg-blue-100 text-blue-700 rounded-sm font-mono text-[10px]" : "bg-amber-100 text-amber-700 rounded-sm font-mono text-[10px]"}>
                    {selected.auth === "oauth" ? (<><Lock size={10} className="mr-1"/> OAUTH</>) : (<><KeyRound size={10} className="mr-1"/> API KEY</>)}
                  </Badge>
                </div>
                <DialogDescription className="sr-only">
                  Détails et options de connexion pour l&apos;intégration {selected.name}.
                </DialogDescription>
              </DialogHeader>

              <p className="text-sm text-zinc-600 leading-relaxed mb-6">{selected.description}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="overline mb-3">Actions disponibles</p>
                  <ul className="space-y-2">
                    {selected.actions?.map((a) => (
                      <li key={a.id} className="flex items-start gap-2 text-sm">
                        <Zap size={14} className="klein-text mt-0.5 shrink-0"/>
                        <span>{a.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="overline mb-3">Déclencheurs</p>
                  <ul className="space-y-2">
                    {selected.triggers?.map((t) => (
                      <li key={t.id} className="flex items-start gap-2 text-sm">
                        <ArrowRight size={14} className="text-emerald-600 mt-0.5 shrink-0"/>
                        <span>{t.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Live connection test result */}
              {connectionTest && connectionTest.slug === selected.slug && (
                <div className={`border rounded-sm p-3 mt-4 text-xs font-mono ${connectionTest.success ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="connection-test-result">
                  <p className="font-medium mb-1">{connectionTest.success ? "✓ Connexion validée" : "✗ Connexion échouée"}</p>
                  <p className="text-[11px] opacity-80 whitespace-pre-wrap break-all">{connectionTest.message || connectionTest.error}</p>
                  {connectionTest.account_label && <p className="mt-1">Compte : <span className="font-bold">{connectionTest.account_label}</span></p>}
                  {connectionTest.connected_at && <p>Connecté le : {new Date(connectionTest.connected_at).toLocaleString("fr-FR")}</p>}
                  {connectionTest.live_response && (
                    <details className="mt-2 cursor-pointer">
                      <summary className="text-[10px] underline">Détails API en direct</summary>
                      <pre className="text-[10px] mt-1 whitespace-pre-wrap break-all bg-white/60 p-2 rounded-sm">{JSON.stringify(connectionTest.live_response, null, 2)}</pre>
                    </details>
                  )}
                </div>
              )}

              <div className="border-t border-zinc-200 pt-4 mt-6 flex items-center justify-between">
                <p className="text-xs text-zinc-500">
                  {selected.auth === "oauth" ? "🔐 Connexion OAuth sécurisée" : "🔑 Clé API requise"}
                </p>
                <div className="flex items-center gap-2">
                  {connected[selected.slug] && (
                    <Button
                      onClick={() => testConnection(selected.slug)}
                      disabled={testingConnection}
                      className="rounded-sm bg-white border border-zinc-300 text-black hover:bg-zinc-50"
                      data-testid="integration-modal-test-connection"
                    >
                      {testingConnection ? "Test..." : "Tester la connexion"}
                    </Button>
                  )}
                  {connected[selected.slug] && (
                    <Button
                      onClick={() => { reconnect(selected.slug); setSelected(null); }}
                      className="rounded-sm bg-white border border-zinc-300 text-black hover:bg-zinc-50"
                      data-testid="integration-modal-reconnect"
                    >
                      Reconnecter
                    </Button>
                  )}
                  <Button
                    onClick={() => { toggle(selected.slug); setSelected(null); }}
                    className={connected[selected.slug] ? "rounded-sm bg-white border border-zinc-300 text-black hover:bg-zinc-50" : "rounded-sm bg-black text-white hover:bg-zinc-800"}
                    data-testid="integration-modal-toggle"
                  >
                    {connected[selected.slug] ? "Déconnecter" : "Connecter"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* API Key prompt modal (Brevo / Yousign / Pennylane) */}
      <Dialog open={apiKeyModal.open} onOpenChange={(o) => !o && setApiKeyModal({ open: false, slug: null })}>
        <DialogContent className="rounded-sm max-w-md" data-testid="apikey-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-medium tracking-tight flex items-center gap-2">
              <KeyRound size={18} className="text-amber-600"/>
              Connecter {apiKeyModal.slug ? (catalog.integrations.find(i => i.slug === apiKeyModal.slug)?.name || apiKeyModal.slug) : ""}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Saisissez vos identifiants API pour activer cette intégration.
            </DialogDescription>
          </DialogHeader>
          {apiKeyModal.slug && (() => {
            const meta = API_KEY_HELP[apiKeyModal.slug] || { label: "Clé API", placeholder: "sk_...", help: "Collez votre clé API ci-dessous.", url: "" };
            return (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5 block">{meta.label}</label>
                  <Input
                    type="password"
                    value={apiKeyValue}
                    onChange={(e) => setApiKeyValue(e.target.value)}
                    placeholder={meta.placeholder}
                    className="rounded-sm font-mono text-sm"
                    data-testid="apikey-input"
                    autoFocus
                  />
                  <p className="text-xs text-zinc-600 mt-2 leading-relaxed">{meta.help}</p>
                  {meta.url && (
                    <a href={meta.url} target="_blank" rel="noopener noreferrer" className="text-xs klein-text underline mt-1 inline-block">
                      Ouvrir la console {apiKeyModal.slug} →
                    </a>
                  )}
                </div>
                {meta.token_label && (
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5 block">{meta.token_label}</label>
                    <Input
                      type="password"
                      value={apiTokenValue}
                      onChange={(e) => setApiTokenValue(e.target.value)}
                      placeholder={meta.token_placeholder || ""}
                      className="rounded-sm font-mono text-sm"
                      data-testid="apitoken-input"
                    />
                    {meta.token_help && <p className="text-xs text-zinc-600 mt-2 leading-relaxed">{meta.token_help}</p>}
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
                  <Button
                    onClick={() => setApiKeyModal({ open: false, slug: null })}
                    className="rounded-sm bg-white border border-zinc-300 text-black hover:bg-zinc-50"
                    data-testid="apikey-cancel"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={submitApiKey}
                    disabled={savingKey || !apiKeyValue.trim() || (meta.token_label && !apiTokenValue.trim())}
                    className="rounded-sm bg-black text-white hover:bg-zinc-800"
                    data-testid="apikey-submit"
                  >
                    {savingKey ? "Connexion..." : "Connecter"}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Shopify shop-name prompt modal */}
      <Dialog open={shopModal.open} onOpenChange={(o) => !o && setShopModal({ open: false, slug: null })}>
        <DialogContent className="rounded-sm max-w-md" data-testid="shopify-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-medium tracking-tight flex items-center gap-2">
              <Icons.ShoppingBag size={18} className="text-emerald-600"/>
              Connecter Shopify
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Indiquez le sous-domaine de votre boutique Shopify pour démarrer l&apos;authentification.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5 block">Nom de votre boutique</label>
              <div className="flex items-center gap-1">
                <Input
                  value={shopValue}
                  onChange={(e) => setShopValue(e.target.value)}
                  placeholder="monstore"
                  className="rounded-sm font-mono text-sm"
                  data-testid="shopify-shop-input"
                  autoFocus
                />
                <span className="text-xs text-zinc-500 font-mono whitespace-nowrap">.myshopify.com</span>
              </div>
              <p className="text-xs text-zinc-600 mt-2 leading-relaxed">
                Saisissez uniquement le sous-domaine de votre boutique Shopify (ex: <span className="font-mono">monstore</span> pour <span className="font-mono">monstore.myshopify.com</span>).
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
              <Button
                onClick={() => setShopModal({ open: false, slug: null })}
                className="rounded-sm bg-white border border-zinc-300 text-black hover:bg-zinc-50"
                data-testid="shopify-cancel"
              >
                Annuler
              </Button>
              <Button
                onClick={submitShop}
                disabled={startingShop || !shopValue.trim()}
                className="rounded-sm bg-black text-white hover:bg-zinc-800"
                data-testid="shopify-submit"
              >
                {startingShop ? "Connexion..." : "Continuer vers Shopify"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
